import type { QueryClient } from '@tanstack/react-query';
import { getVenueInquiries, saveInquiry } from 'api';
import {
    cancelAndCopyInquiry,
    createInquiryDraft,
    getCurrentInquiry,
    removeVenueFromInquiry,
    submitInquiry,
    updateBookingInquiry,
} from 'api/inquiry';
import { BookedState } from 'components/VenueTile/VenueCard';
import { GET_EVENT, GET_EVENTS } from 'constants/queryKeys';
import { HAS_ACCEPTED_PROPOSAL_STATUS, INQUIRY_CANCELLED_OR_EXPIRED, VenueStatus } from 'constants/venueStatus';
import { useMemo } from 'react';
import { eventActions } from 'stores/event';
import { getVenueId, InquiryVenue, MAX_QUEUE_VENUE_SLOTS } from 'utils/venue';
import { create } from 'zustand';

/** Refreshes the zustand event store and invalidates the TanStack Query caches that surface event status. */
function refreshEventStatus(queryClient: QueryClient, eventId: number) {
    eventActions.refreshEvent(eventId);
    queryClient.invalidateQueries({ queryKey: [GET_EVENT, eventId] });
    queryClient.invalidateQueries({ queryKey: [GET_EVENTS] });
}

function computeAvailableQueueSlots(venues: Bizly.Venue[]): number {
    const everPromotedCount = venues.filter(v => v.wasEverPromotedFromQueue).length;
    return Math.max(0, MAX_QUEUE_VENUE_SLOTS - everPromotedCount);
}

export const NEW_INQUIRY_ID = 'new';

type State = {
    loading: boolean;
    updatingVenues: boolean;
} & (
    | {
          loaded: false;
          eventId?: number;
          inquiry: null;
          venues: null;
          queuedVenues: null;
          availableQueueSlots: null;
          historicalInactiveVenues: null;
      }
    | {
          loaded: true;
          eventId: number;
          inquiry: null | BizlyAPI.Inquiry;
          venues: Bizly.Venue[];
          queuedVenues: InquiryVenue[];
          availableQueueSlots: number;
          historicalInactiveVenues: Bizly.Venue[];
      }
);
type Store = State;

const initialState: State = {
    inquiry: null,
    venues: null,
    queuedVenues: null,
    availableQueueSlots: null,
    historicalInactiveVenues: null,
    loaded: false,
    loading: false,
    updatingVenues: false,
};

export const useCurrentInquiry = create<Store>(() => initialState);

const { setState, getState } = useCurrentInquiry;

/** Returns the accepted venue (Contract Pending, Booked, or Booking Cancelled) from the current inquiry, or null. */
export function useAcceptedVenue(): { acceptedVenue: Bizly.Venue | null; loaded: boolean } {
    const { venues, loaded } = useCurrentInquiry();
    const acceptedVenue = useMemo(() => {
        if (!venues?.length) return null;
        return venues.find(v => HAS_ACCEPTED_PROPOSAL_STATUS.has(v.status as BookedState)) ?? null;
    }, [venues]);
    return { acceptedVenue, loaded: loaded ?? false };
}

/** Normalize venues for store: derive queuedVenues from raw API venues. */
function normalizeVenues(venues: Bizly.Venue[]) {
    const normalized = venues || [];
    const queuedVenues: InquiryVenue[] = normalized
        .filter(v => v.status === VenueStatus.Queued)
        .map(v => {
            const inner = v.venue ?? v;
            return {
                id: v.id,
                status: 'Queued' as const,
                queuedAt: v.queuedAt ?? null,
                inquiryId: v.inquiryId ?? null,
                venue: inner as Omit<Bizly.Venue, 'venue'>,
            };
        });
    return { venues: normalized, queuedVenues };
}

export const hasAcceptedProposal = (venuesInqs: Bizly.Venue[]) =>
    venuesInqs.filter(
        venue =>
            venue.status === 'Booked' || venue.status === 'Contract Pending' || venue.status === 'Booking Cancelled'
    ).length > 0;

export const hasBookedVenue = (venuesInqs: Bizly.Venue[]) =>
    venuesInqs.filter(venue => venue.status === 'Booked' || venue.status === 'Booking Cancelled').length > 0;

export function getHistoricalInactiveVenues(
    eventInquiryVenues: Bizly.Venue[],
    currentInquiryId: number
): Bizly.Venue[] {
    const currentVenues = eventInquiryVenues.filter(v => v.inquiryId === currentInquiryId);
    const currentListingIds = new Set(currentVenues.map(v => v.venue.id));
    const fromOlderInquiries: Bizly.Venue[] = [];
    const seenOldListingIds = new Set<number>();
    for (const v of eventInquiryVenues) {
        if (
            v.inquiryId === currentInquiryId ||
            !INQUIRY_CANCELLED_OR_EXPIRED.has(v.status) ||
            currentListingIds.has(v.venue.id) ||
            seenOldListingIds.has(v.venue.id)
        ) {
            continue;
        }
        seenOldListingIds.add(v.venue.id);
        fromOlderInquiries.push(v);
    }
    return fromOlderInquiries;
}

export function splitEventVenuesForCurrentInquiry(
    eventInquiryVenues: Bizly.Venue[],
    currentInquiryId: number
): { venues: Bizly.Venue[]; historicalInactiveVenues: Bizly.Venue[] } {
    const venues = eventInquiryVenues.filter(v => v.inquiryId === currentInquiryId);
    return {
        venues,
        historicalInactiveVenues: getHistoricalInactiveVenues(eventInquiryVenues, currentInquiryId),
    };
}

const EMPTY_DISPLAY_VENUES: Bizly.Venue[] = [];

export function useDisplayInquiryVenues(): Bizly.Venue[] {
    const loaded = useCurrentInquiry(s => s.loaded);
    const venues = useCurrentInquiry(s => (s.loaded ? s.venues : EMPTY_DISPLAY_VENUES));
    const historicalInactiveVenues = useCurrentInquiry(s =>
        s.loaded ? s.historicalInactiveVenues : EMPTY_DISPLAY_VENUES
    );
    return useMemo(() => {
        if (!loaded) return EMPTY_DISPLAY_VENUES;
        return [...venues, ...historicalInactiveVenues];
    }, [loaded, venues, historicalInactiveVenues]);
}

export const currentInquiryActions = {
    load: async (eventId: number, forceReload = false) => {
        const { eventId: curEventId, inquiry: curInquiry } = getState();
        if (eventId === curEventId && curInquiry && !forceReload) {
            return curInquiry;
        }

        setState({
            loading: true,
            ...(eventId !== curEventId
                ? {
                      inquiry: null,
                      venues: null,
                      queuedVenues: null,
                      availableQueueSlots: null,
                      historicalInactiveVenues: null,
                      eventId,
                      loaded: false,
                  }
                : {}),
        });

        try {
            const inquiry = await getCurrentInquiry(eventId);
            let venues: Bizly.Venue[] = [];
            let historicalInactiveVenues: Bizly.Venue[] = [];
            if (inquiry) {
                const eventInquiryVenues = await getVenueInquiries(eventId);
                const split = splitEventVenuesForCurrentInquiry(eventInquiryVenues, inquiry.id);
                venues = split.venues;
                historicalInactiveVenues = split.historicalInactiveVenues;
            }

            if (getState().eventId === eventId) {
                const { venues: normalizedVenues, queuedVenues: normalizedQueued } = normalizeVenues(venues);
                setState({
                    inquiry,
                    eventId,
                    loaded: true,
                    loading: false,
                    venues: normalizedVenues,
                    queuedVenues: normalizedQueued,
                    availableQueueSlots: computeAvailableQueueSlots(normalizedVenues),
                    historicalInactiveVenues,
                });
            }

            return inquiry;
        } catch (e) {
            setState({
                loading: false,
            });
            throw e;
        }
    },

    createDraft: async (queryClient: QueryClient, eventId: number) => {
        let currentState = getState();
        if (eventId !== currentState.eventId) return;
        if (currentState.inquiry) throw new Error('Meeting already has an inquiry.');

        setState({ loading: true });

        try {
            const { inquiry } = await createInquiryDraft(eventId);
            currentState = getState();
            if (eventId !== currentState.eventId) return;

            setState({
                inquiry,
                venues: [],
                queuedVenues: [],
                availableQueueSlots: MAX_QUEUE_VENUE_SLOTS,
                historicalInactiveVenues: [],
                eventId,
                loaded: true,
                loading: false,
            });

            refreshEventStatus(queryClient, eventId);

            return inquiry;
        } catch (e) {
            currentState = getState();
            if (eventId !== currentState.eventId) return;

            setState({ loading: false });
            throw e;
        }
    },

    updateDraft: async (inquiryId: number, data: Partial<BizlyAPI.Inquiry>) => {
        let currentState = getState();
        if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;
        if (!currentState.inquiry.draft) throw new Error('Can only edit unsubmitted inquiries');

        setState({ loading: true });

        try {
            const { inquiry } = await updateBookingInquiry(inquiryId, data);

            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            setState({
                inquiry,
                loading: false,
            });

            return inquiry;
        } catch (e) {
            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            setState({ loading: false });
            throw e;
        }
    },

    createAndUpdateDraft: async (queryClient: QueryClient, eventId: number, data: Partial<BizlyAPI.Inquiry>) => {
        const inquiry = await currentInquiryActions.createDraft(queryClient, eventId);
        if (!inquiry) return;
        const inquiryId = inquiry.id;
        return await currentInquiryActions.updateDraft(inquiryId, data);
    },

    setInquiry: (eventId: number, inquiry: BizlyAPI.Inquiry) => {
        setState({
            inquiry,
            eventId,
            loaded: getState().venues ? true : undefined,
        });
    },

    mergeVenues: (eventId: number, updatedVenues: Bizly.Venue[]) => {
        const currentState = getState();
        if (!currentState.loaded || !currentState.inquiry || eventId !== currentState.eventId) return;

        /** Deduplicate by listing id: optimistic queue merges use `venue.id` as listing id; API rows use inquiry join `id` with nested `venue.id`. */
        const updatedListingIds = new Set(updatedVenues.map(venue => getVenueId(venue)));
        const merged = [
            ...currentState.venues.filter(venue => !updatedListingIds.has(getVenueId(venue))),
            ...updatedVenues,
        ];
        const { venues: normalizedVenues, queuedVenues: normalizedQueued } = normalizeVenues(merged);

        setState({
            venues: normalizedVenues,
            queuedVenues: normalizedQueued,
            availableQueueSlots: computeAvailableQueueSlots(normalizedVenues),
            loaded: getState().inquiry ? true : undefined,
        });
    },

    /** Refetch venues from API and update store (e.g. after cancelling an inquiry). Does not set loading state. */
    refetchVenues: async (eventId: number) => {
        const currentState = getState();
        if (!currentState.loaded || !currentState.inquiry || eventId !== currentState.eventId) return;

        try {
            const eventInquiryVenues = await getVenueInquiries(eventId);
            const { venues, historicalInactiveVenues } = splitEventVenuesForCurrentInquiry(
                eventInquiryVenues,
                currentState.inquiry.id
            );
            currentInquiryActions.setVenues(venues, historicalInactiveVenues);
        } catch {
            // Keep existing venues on refetch error
        }
    },

    removeVenueInq: (eventId: number, targetVenueId: number) => {
        const currentState = getState();
        if (!currentState.loaded || !currentState.inquiry || eventId !== currentState.eventId) return;

        const isRemovable = (status: string | undefined) =>
            status === VenueStatus.Added || status === VenueStatus.Queued;
        const toDelete = currentState.venues.find(
            venue => getVenueId(venue) === targetVenueId && isRemovable(venue.status)
        );
        if (!toDelete) return;

        const nextVenues = currentState.venues.filter(venue => {
            if (getVenueId(venue) === targetVenueId && isRemovable(venue.status)) return false;
            return true;
        });
        const { venues: normalizedVenues, queuedVenues: normalizedQueued } = normalizeVenues(nextVenues);
        setState({
            venues: normalizedVenues,
            queuedVenues: normalizedQueued,
            availableQueueSlots: computeAvailableQueueSlots(normalizedVenues),
        });
    },

    removeVenue: async (inquiryId: number, venueId: number) => {
        let currentState = getState();
        if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;
        if (!currentState.inquiry.draft) throw new Error('Can only edit unsubmitted inquiries');

        setState({ loading: true });

        try {
            const { inquiry } = await removeVenueFromInquiry(inquiryId, venueId);

            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            const nextVenues = currentState.venues.filter(venue => getVenueId(venue) !== venueId);
            const { venues: normalizedVenues, queuedVenues: normalizedQueued } = normalizeVenues(nextVenues);
            setState({
                inquiry,
                venues: normalizedVenues,
                queuedVenues: normalizedQueued,
                availableQueueSlots: computeAvailableQueueSlots(normalizedVenues),
                loading: false,
            });

            return inquiry;
        } catch (e) {
            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            setState({ loading: false });
            throw e;
        }
    },

    /**
     * Submits the inquiry and refreshes venue/queue state.
     * @param queryClient — Used to invalidate the events list/detail caches once the event's status changes.
     * @param inquiryId — The inquiry ID to submit.
     * @param eventId — Required. The event's ID, used to call getVenueInquiries(eventId) after submission to refresh the venue/queue state. Must not be omitted — passing undefined will silently fail to update queue state.
     */
    submit: async (queryClient: QueryClient, inquiryId: number, eventId: number) => {
        let currentState = getState();
        if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;
        if (!currentState.inquiry.draft) throw new Error('Can only edit unsubmitted inquiries');

        setState({ loading: true });

        try {
            const { inquiry } = await submitInquiry(inquiryId);

            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            const eventInquiryVenues = await getVenueInquiries(eventId);
            const { venues, historicalInactiveVenues } = splitEventVenuesForCurrentInquiry(
                eventInquiryVenues,
                inquiry.id
            );
            const { venues: normalizedVenues, queuedVenues: normalizedQueued } = normalizeVenues(venues);
            setState({
                inquiry,
                loading: false,
                venues: normalizedVenues,
                queuedVenues: normalizedQueued,
                availableQueueSlots: computeAvailableQueueSlots(normalizedVenues),
                historicalInactiveVenues,
            });

            refreshEventStatus(queryClient, eventId);

            return inquiry;
        } catch (e) {
            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            setState({ loading: false });
            throw e;
        }
    },

    /**
     * Updates the inquiry draft and submits it.
     * @param queryClient — Used to invalidate the events list/detail caches once the event's status changes.
     * @param inquiryId — The inquiry ID to update and submit.
     * @param data — Partial inquiry data to apply before submission.
     * @param eventId — Required. Forwarded to submit; used to refresh venue and queue state after submission. Must not be omitted.
     */
    updateAndSubmit: async (
        queryClient: QueryClient,
        inquiryId: number,
        data: Partial<BizlyAPI.Inquiry>,
        eventId: number
    ) => {
        const inquiry = await currentInquiryActions.updateDraft(inquiryId, data);
        if (!inquiry) return;
        return await currentInquiryActions.submit(queryClient, inquiryId, eventId);
    },

    cancelAndCopy: async (queryClient: QueryClient, inquiryId: number | string) => {
        let currentState = getState();
        if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;
        if (currentState.inquiry.draft) throw new Error('Can only copy a submitted inquiry');

        setState({ loading: true });

        try {
            const { inquiry } = await cancelAndCopyInquiry(inquiryId);

            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            const eventInquiryVenues = await getVenueInquiries(currentState.eventId);
            const { venues, historicalInactiveVenues } = splitEventVenuesForCurrentInquiry(
                eventInquiryVenues,
                inquiry.id
            );
            const { venues: normalizedVenues, queuedVenues: normalizedQueued } = normalizeVenues(venues);
            setState({
                inquiry,
                loading: false,
                venues: normalizedVenues,
                queuedVenues: normalizedQueued,
                availableQueueSlots: computeAvailableQueueSlots(normalizedVenues),
                historicalInactiveVenues,
            });

            refreshEventStatus(queryClient, currentState.eventId);

            return inquiry;
        } catch (e) {
            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            setState({ loading: false });
            throw e;
        }
    },
    setVenues: (venues: Bizly.Venue[], historicalInactiveVenues?: Bizly.Venue[]) => {
        const s = getState();
        if (!s.loaded) return;
        const { venues: normalizedVenues, queuedVenues: normalizedQueued } = normalizeVenues(venues);
        setState({
            venues: normalizedVenues,
            queuedVenues: normalizedQueued,
            availableQueueSlots: computeAvailableQueueSlots(normalizedVenues),
            ...(historicalInactiveVenues !== undefined ? { historicalInactiveVenues } : {}),
        });
    },

    setUpdatingVenues: (updatingVenues: boolean) => {
        setState({ updatingVenues });
    },
    setLoadingInquiry: (loading: boolean) => {
        setState({ loading });
    },
    updateVenue: (venue: Bizly.Venue) => {
        const currentState = getState();
        if (currentState.venues && currentState.venues.length > 0) {
            setState({
                venues: currentState.venues.map(v =>
                    getVenueId(v) === getVenueId(venue) ? { ...v, venue: { ...v.venue, ...venue } } : v
                ),
            });
        }
    },
    saveInquiry: async (inquiryId: number) => {
        let currentState = getState();
        if (!currentState.inquiry || inquiryId !== currentState.inquiry.id) return;
        if (!currentState.inquiry.draft) throw new Error('Can only edit unsubmitted inquiries');

        setState({ loading: true });
        try {
            const { inquiry } = await saveInquiry(inquiryId);

            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            setState({ inquiry, loading: false });
        } catch (e) {
            currentState = getState();
            if (!currentState.loaded || !currentState.inquiry || inquiryId !== currentState.inquiry.id) return;

            setState({ loading: false });
            throw e;
        }
    },
};
